/**
 * Renders a team crest. Falls back to a neutral white-flag glyph when no
 * image is available.
 *
 * Backend `team.logo` may be:
 *  - a remote URL (e.g. football-data.org `crest`)  → render as <img>
 *  - an emoji (legacy seed data, e.g. "🏴")         → render as text
 *  - missing                                        → fallback "🏳️"
 */
function isUrl(value) {
    return typeof value === "string" && /^https?:\/\//i.test(value);
}

export default function TeamCrest({ logo, name, size = "md", className = "", backdrop = true }) {
    const dims = {
        sm: { box: "h-6 w-6", text: "text-lg", pad: "p-0.5" },
        md: { box: "h-10 w-10", text: "text-2xl", pad: "p-1" },
        lg: { box: "h-14 w-14", text: "text-4xl", pad: "p-1.5" },
    }[size] || { box: "h-10 w-10", text: "text-2xl", pad: "p-1" };

    if (isUrl(logo)) {
        const img = (
            <img
                src={logo}
                alt={name ? `${name} crest` : "team crest"}
                className={`${dims.box} object-contain inline-block ${className}`}
                loading="lazy"
            />
        );
        if (!backdrop) return img;
        // Subtle white chip behind the crest so dark / transparent federation
        // emblems stay visible on both light and dark themes.
        return (
            <span className={`inline-flex items-center justify-center rounded-full bg-white/95 dark:bg-white/90 shadow-sm ring-1 ring-black/5 ${dims.pad}`}>
                {img}
            </span>
        );
    }
    return (
        <span className={`${dims.text} inline-block leading-none ${className}`} aria-hidden>
            {logo || "🏳️"}
        </span>
    );
}
